import type { UserPreferences } from "@plannello/shared";
import { api } from "./api";
import {
  densityPref,
  type Pref,
  showArchivedPref,
  sidebarCollapsedPref,
  singleKeyShortcutsPref,
  themePref,
} from "./prefs";

/**
 * Server-side preferences (GET/PATCH /api/v1/me/preferences) behind the localStorage prefs of prefs.ts.
 *
 * localStorage stays the fast first-paint cache (theme-init.js reads it before React runs). On sign-in
 * `syncPreferences` loads the server copy, which wins per key; keys the server has never seen are filled from
 * this browser's stored choices once (migration), unless another account used this browser last. Afterwards every
 * change of a synced pref is saved with a debounced PATCH. Failures are silent: the cache keeps working and the
 * next change retries with the full pending patch.
 */

const SAVE_DELAY_MS = 600;
const USER_KEY = "pt-prefs-user";

type Entry = {
  /** Key in the server object (`ui.<key>` when `ui` is set). */
  key: string;
  ui?: boolean;
  pref: Pref<string | boolean>;
  fallback: string | boolean;
};

/** The stores differ in their value type; here they are only read and written as string or boolean. */
const loose = (p: unknown) => p as Pref<string | boolean>;

function entries(): Entry[] {
  return [
    { key: "theme", pref: loose(themePref), fallback: "system" },
    { key: "density", pref: loose(densityPref), fallback: "compact" },
    { key: "sidebarCollapsed", pref: loose(sidebarCollapsedPref), fallback: false },
    { key: "singleKeyShortcuts", pref: loose(singleKeyShortcutsPref), fallback: true },
    { key: "showArchived", ui: true, pref: loose(showArchivedPref), fallback: false },
  ];
}

let activeUser: string | null = null;
let unsubscribe: Array<() => void> = [];
let applying = false;
let pending: { known: Record<string, unknown>; ui: Record<string, unknown> } = { known: {}, ui: {} };
let timer: number | undefined;

function readStoredUser(): string | null {
  try {
    return localStorage.getItem(USER_KEY);
  } catch {
    return null;
  }
}

function hasPending() {
  return Object.keys(pending.known).length + Object.keys(pending.ui).length > 0;
}

function takePatch(): UserPreferences | null {
  if (!hasPending()) return null;
  const patch: Record<string, unknown> = { ...pending.known };
  if (Object.keys(pending.ui).length > 0) patch.ui = pending.ui;
  pending = { known: {}, ui: {} };
  return patch as UserPreferences;
}

async function flush() {
  window.clearTimeout(timer);
  timer = undefined;
  const patch = takePatch();
  if (!patch) return;
  try {
    await api.patch("/api/v1/me/preferences", patch);
  } catch {
    // Keep what failed so the next change sends it again.
    pending.known = { ...(patch as Record<string, unknown>), ...pending.known };
    delete pending.known.ui;
    pending.ui = { ...((patch as { ui?: Record<string, unknown> }).ui ?? {}), ...pending.ui };
  }
}

function queue(e: Entry, value: string | boolean) {
  if (e.ui) pending.ui[e.key] = value;
  else pending.known[e.key] = value;
  window.clearTimeout(timer);
  timer = window.setTimeout(() => void flush(), SAVE_DELAY_MS);
}

/** Saves what is pending right now (tab hidden or closing); keepalive lets the request outlive the page. */
function flushNow() {
  const patch = takePatch();
  if (!patch) return;
  void fetch("/api/v1/me/preferences", {
    method: "PATCH",
    credentials: "same-origin",
    keepalive: true,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(patch),
  }).catch(() => undefined);
}

let hideBound = false;
function bindHide() {
  if (hideBound) return;
  hideBound = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushNow();
  });
  window.addEventListener("pagehide", flushNow);
}

/** Loads the server preferences for `userId`, applies them locally and starts saving changes. Never throws. */
export async function syncPreferences(userId: string): Promise<void> {
  if (activeUser === userId) return;
  for (const off of unsubscribe) off();
  unsubscribe = [];
  window.clearTimeout(timer);
  pending = { known: {}, ui: {} };
  activeUser = userId;

  const list = entries();
  const lastUser = readStoredUser();
  const switchedAccount = lastUser !== null && lastUser !== userId;
  applying = true;
  try {
    // Another account used this browser: its cached choices are not this user's.
    if (switchedAccount) for (const e of list) e.pref.set(e.fallback);
  } finally {
    applying = false;
  }
  try {
    localStorage.setItem(USER_KEY, userId);
  } catch {
    // private mode
  }

  let server: UserPreferences = {};
  try {
    server = await api.get<UserPreferences>("/api/v1/me/preferences", AbortSignal.timeout(4000));
  } catch {
    // Offline or slow: keep the cache and still save later changes.
  }
  if (activeUser !== userId) return;

  const migrate: Record<string, unknown> = {};
  const migrateUi: Record<string, unknown> = {};
  applying = true;
  try {
    for (const e of list) {
      const remote = e.ui
        ? (server.ui as Record<string, unknown> | undefined)?.[e.key]
        : server[e.key as keyof UserPreferences];
      if (remote !== undefined && typeof remote === typeof e.fallback) {
        e.pref.set(remote as string | boolean);
      } else if (e.pref.get() !== e.fallback) {
        (e.ui ? migrateUi : migrate)[e.key] = e.pref.get();
      }
    }
  } finally {
    applying = false;
  }
  if (Object.keys(migrate).length + Object.keys(migrateUi).length > 0) {
    Object.assign(pending.known, migrate);
    Object.assign(pending.ui, migrateUi);
    void flush();
  }

  for (const e of list) {
    unsubscribe.push(
      e.pref.subscribe(() => {
        if (!applying) queue(e, e.pref.get());
      }),
    );
  }
  bindHide();
}

/** Forget the signed-in user (sign-out): stops saving; the next sign-in syncs again. */
export function stopPreferencesSync() {
  for (const off of unsubscribe) off();
  unsubscribe = [];
  window.clearTimeout(timer);
  pending = { known: {}, ui: {} };
  activeUser = null;
}
